import type { ComponentProps } from 'react';
import React, { useCallback } from 'react';
import styled from '@emotion/styled';
import type { WidthProps } from 'styled-system';
import { space, width } from 'styled-system';

import { CssGrid } from 'common/components/CssGrid';
import { Grid, GridItem } from 'common/components/Grid';
import { BodyRegularBR2, HeadlineH7 } from 'common/components/Typography';
import type { SpaceProps } from 'common/theme/system-types';
import type { ListStyleProps } from 'common/theme/system-utilities';
import { listStyle } from 'common/theme/system-utilities';
import { dispatchTrackingEventGA4 } from 'common/tracking/dispatchTrackingEventGA4';
import type { DyTrackingGA4Props } from 'common/tracking/getDyTrackingGA4Params';
import { getDyTrackingGA4Params } from 'common/tracking/getDyTrackingGA4Params';
import type { SeeAllLinkProp, VisualNavigationItemData } from 'common/types/visual-navigation';
import { SeeAllLink } from '../../list-navigation/components/SeeAllLink';
import { useTrackingEvents, useViewTrackingEvent } from './hooks/tracking';
import { VerticalCategoryItem } from './VerticalCategoryItem';

const UnstyledSeeAllLink = styled(SeeAllLink)<SpaceProps & WidthProps>`
  ${space};
  ${width};

  &:hover {
    text-decoration: none;
  }
`;

const RightAlignedSeeAllLink = (props: ComponentProps<typeof UnstyledSeeAllLink>) => (
  <UnstyledSeeAllLink ml="auto" width="fit-content" {...props} />
);

interface VerticalCategoryGridProps extends SeeAllLinkProp {
  items: VisualNavigationItemData[];
  title?: string;
  subtitle?: string;
  tracking?: DyTrackingGA4Props;
}

const UnstyledList = styled.ul<ListStyleProps & SpaceProps>`
  ${listStyle};
  ${space};

  & > li {
    min-width: 0;
  }
`;

const List = (props: ComponentProps<typeof UnstyledList>) => (
  <UnstyledList listStyle="none" p="sp_0" m="sp_0" {...props} />
);

const GRID_COLUMNS = ['repeat(2, 1fr)', 'repeat(3, 1fr)', null, 'repeat(4, 1fr)'];
const GRID_GAP = ['sp_16', 'sp_24', null, 'sp_32'];

export const VerticalCategoryGrid = ({
  items,
  title,
  subtitle,
  seeAllLink,
  tracking,
}: VerticalCategoryGridProps) => {
  const { ref } = useViewTrackingEvent({
    title,
    items,
    activeSlide: 0,
    slidesToShow: items.length,
    tracking,
  });

  const { onSeeAllLinkClick } = useTrackingEvents({
    title,
    linkText: seeAllLink?.label,
    linkUrl: seeAllLink?.href,
    tracking,
  });

  const dyTrackingParams = getDyTrackingGA4Params(tracking);

  const onItemClick = useCallback(
    (index: number, description?: string) => {
      dispatchTrackingEventGA4({
        event: 'common-visual-navigation_click',
        element: 'image',
        title: title ?? '',
        list_position: index,
        list_length: items.length,
        list_item: description,
        ...dyTrackingParams,
      });
    },
    [title, items, dyTrackingParams]
  );

  return (
    <Grid
      py={['sp_16', 'sp_20', 'sp_24', 'sp_32']}
      bg="sys.neutral.background.default"
      data-testid="vertical-category-grid"
      px={0}
    >
      <GridItem px={0}>
        <div ref={ref} />
        {title && <HeadlineH7 mb={subtitle ? 'sp_8' : 'sp_16'}>{title}</HeadlineH7>}
        {subtitle && (
          <BodyRegularBR2
            mb={['sp_16', 'sp_20', null, 'sp_24']}
            data-testid="vertical-category-grid-subtitle"
          >
            {subtitle}
          </BodyRegularBR2>
        )}
        <nav aria-label={title}>
          <CssGrid as={List} gridTemplateColumns={GRID_COLUMNS} gridGap={GRID_GAP}>
            {items.map((item, index) => (
              <li key={index}>
                <VerticalCategoryItem
                  {...item}
                  index={index}
                  onClick={() => onItemClick(index, item.description)}
                />
              </li>
            ))}
          </CssGrid>
        </nav>
        {seeAllLink?.href && seeAllLink?.label && (
          <RightAlignedSeeAllLink
            mt={['sp_16', 'sp_20', null, 'sp_24']}
            href={seeAllLink.href}
            onClick={onSeeAllLinkClick}
          >
            {seeAllLink.label}
          </RightAlignedSeeAllLink>
        )}
      </GridItem>
    </Grid>
  );
};
